import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Link,
  Paper,
  Typography
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon
} from '@mui/icons-material';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import api from "../../api/axiosInstance";
import { useNavigate } from 'react-router-dom';
import { useProfile } from "../../providers/ProfileProvider";

const FILE_CONSTRAINTS = {
  MAX_SIZE: 3 * 1024 * 1024,
  ALLOWED_TYPES: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ],
  ACCEPT_ATTR: '.pdf,.doc,.docx'
};

const STORAGE_KEYS = {
  UPLOADED_FILES: 'uploadedCVFiles',
  FILES_DATA: 'cvFilesData',
  AUTH_TOKEN: 'token'
};

const useFileStorage = (key, initialValue = []) => {
  const [files, setFiles] = useState(initialValue);

  const setStoredFiles = useCallback((newFiles) => {
    setFiles(newFiles);
    
    try {
      const metadata = newFiles.map(fileObj => ({
        name: fileObj.name,
        size: fileObj.size,
        type: fileObj.type,
        uploadDate: fileObj.uploadDate,
        id: fileObj.id
      }));
      localStorage.setItem(`${key}_metadata`, JSON.stringify(metadata));
    } catch (error) {}
  }, [key]);

  return [files, setStoredFiles];
};

const useDragAndDrop = (onFileSelect, disabled = false) => {
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && (e.type === 'dragenter' || e.type === 'dragover')) {
      setDragActive(true);
    } else {
      setDragActive(false);
    }
  }, [disabled]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || !e.dataTransfer.files?.[0]) return;
    setDragActive(false);
    onFileSelect(e.dataTransfer.files[0]);
  }, [disabled, onFileSelect]);

  return {
    dragActive,
    dragHandlers: {
      onDragEnter: handleDrag,
      onDragLeave: handleDrag,
      onDragOver: handleDrag,
      onDrop: handleDrop
    }
  };
};

const fileUtils = {
  validate: (file) => {
    if (!file) return 'No file provided';
    if (!(file instanceof File)) return 'Invalid file object';
    if (file.size > FILE_CONSTRAINTS.MAX_SIZE) return 'File size must be less than 3MB';
    if (!FILE_CONSTRAINTS.ALLOWED_TYPES.includes(file.type)) return 'Only PDF and Word documents are allowed';
    if (file.size === 0) return 'File is empty';
    return null;
  },

  formatSize: (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  },

  createFileObject: (file) => {
    if (!(file instanceof File)) throw new Error('Invalid file object');
    return {
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      uploadDate: new Date().toISOString(),
      id: `${file.name}-${file.size}-${Date.now()}`
    };
  }
};

const apiService = {
  uploadFiles: async (files, userId) => {
    if (!files || files.length === 0) throw new Error('No files provided for upload');

    const fileObj = files[0];
    if (!fileObj || !fileObj.file) throw new Error('Invalid file object - missing file property');
    if (!(fileObj.file instanceof File)) throw new Error('Invalid file type - not a File object');
    if (fileObj.file.size === 0) throw new Error('File is empty');

    const formData = new FormData();
    formData.append('file', fileObj.file, fileObj.file.name);
    formData.append('fileType', 'cv');
    formData.append('userId', userId);    

    try {
      const response = await api.post('/files/upload', formData,{
        timeout: 30000,
        maxContentLength: 10 * 1024 * 1024,
        maxBodyLength: 10 * 1024 * 1024
      });

      return response.data;
    } catch (error) {
      const status = error.response?.status;
      switch (status) {
        case 401: throw new Error('Your session has expired. Please log in again.');
        case 403: throw new Error('You do not have permission to upload files. Please contact support.');
        case 400: throw new Error(error.response?.data?.message || 'Bad request - please check your file and try again.');
        case 404: throw new Error('Upload endpoint not found. Please contact support.');
        case 413: throw new Error('File is too large. Please use a file smaller than 3MB.');
        case 500: throw new Error('Server error. Please try again in a few moments.');
        default: throw new Error(`Upload failed: ${error.message || 'An unknown error occurred'}`);
      }
    }
  }
};

const FileUploadZone = ({
  uploadedFiles, persistedUpload, dragActive, dragHandlers, onBrowseClick, fileInputRef
}) => {
  const hasFiles = uploadedFiles.length > 0 || persistedUpload;
  const isDisabled = hasFiles;

  return (
    <Paper
      sx={{
        border: hasFiles
          ? '2px solid #E84610'
          : dragActive ? '2px solid #E84610' : '2px dashed #d0d0d0',
        borderRadius: 2,
        p: 6,
        textAlign: 'center',
        backgroundColor: hasFiles 
          ? '#EAE7E6'
          : dragActive ? '#fff3f0' : '#fafafa',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.3s ease',
        mb: 2,
        opacity: isDisabled ? 0.6 : 1,
        '&:hover': !isDisabled ? { borderColor: '#E84610', backgroundColor: '#fff8f5' } : {}
      }}
      {...(!isDisabled && dragHandlers)}
      onClick={!isDisabled ? onBrowseClick : undefined}
    >
      <CloudUploadIcon sx={{ fontSize: 48, color: isDisabled ? '#E84610' : '#2196F3', mb: 2 }} />
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 500, color: isDisabled ? '#E84610' : '#333' }}>
        {hasFiles ? 'CV uploaded successfully!' : <>
          <Link component="span" sx={{ color: '#E84610', textDecoration: 'underline', cursor: 'pointer', mr: 0.5 }}>
            Click to upload
          </Link> or drag and drop
        </>}
      </Typography>
      <Typography variant="body2" sx={{ color: isDisabled ? '#000000' : '#666', fontSize: '14px' }}>
        {hasFiles ? 'Remove file below to upload a different one' : 'PDF, Microsoft Word (max. 3MB) - Optional'}
      </Typography>

      <input ref={fileInputRef} type="file" accept={FILE_CONSTRAINTS.ACCEPT_ATTR} style={{ display: 'none' }} disabled={isDisabled} />
    </Paper>
  );
};

const UploadedFileDisplay = ({ file, onRemove, isPersisted = false }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="h6" sx={{ mb: 2, fontSize: '16px', fontWeight: 500 }}>
      Uploaded CV File
    </Typography>
    <Paper sx={{ border: '2px solid #E84610', borderRadius: 2, p: 3, backgroundColor: '#EAE7E6' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FileIcon sx={{ color: '#E84610', fontSize: 32 }} />
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#333', mb: 0.5 }}>
              {file.name}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', fontSize: '12px' }}>
              {fileUtils.formatSize(file.size)} • {isPersisted ? 'Previously uploaded' : 'Ready to upload'}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onRemove} sx={{ color: '#f44336', '&:hover': { backgroundColor: '#ffebee' } }}>
          <DeleteIcon />
        </IconButton>
      </Box>
    </Paper>
  </Box>
);

const FormActions = ({ onBack, isSubmitting }) => (
  <Box sx={{ 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center', 
    mt: 1, 
    pt: 1 
  }}>
    <Button 
      variant="outlined" 
      startIcon={<ArrowBackIcon />} 
      onClick={onBack} 
      disabled={isSubmitting}
      size="small"
    >
      Back
    </Button>

    <Button 
      type="submit" 
      variant="contained" 
      disabled={isSubmitting} 
      size="small" 
      sx={{ px: 3 }}
    >
      {isSubmitting ? (
        <>
          <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} /> 
          Uploading...
        </>
      ) : 'Next'}
    </Button>
  </Box>
);

const UploadCV = ({ formData, setFormData, onNext, onBack }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [uploadedFiles, setUploadedFiles] = useFileStorage(STORAGE_KEYS.UPLOADED_FILES, []);
  const [uploadError, setUploadError] = useState('');
  const [saveStatus, setSaveStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [persistedUpload, setPersistedUpload] = useState(null);
  const user = useProfile();

  const updateFormData = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, [setFormData]);

  useEffect(() => {
    const savedUpload = localStorage.getItem('cvUploadResult');
    if (savedUpload) {
      try {
        const uploadData = JSON.parse(savedUpload);
        setPersistedUpload(uploadData);
        updateFormData('cvFile', uploadData);
      } catch (error) {
        localStorage.removeItem('cvUploadResult');
      }
    }
  }, [updateFormData]);

  const handleFileSelect = useCallback((file) => {
    setUploadError('');
    if (uploadedFiles.length > 0 || persistedUpload) {
      setUploadError('Only one CV file can be uploaded. Please remove the current file to upload a new one.');
      return;
    }
    
    const validationError = fileUtils.validate(file);
    if (validationError) {
      setUploadError(validationError);
      return;
    }

    try {
      const newFile = fileUtils.createFileObject(file);
      setUploadedFiles([newFile]);
      updateFormData('cvFile', newFile);
    } catch (error) {
      setUploadError(`Error processing file: ${error.message}`);
    }
  }, [uploadedFiles.length, persistedUpload, setUploadedFiles, updateFormData]);

  const handleRemoveFile = useCallback(() => {
    setUploadedFiles([]);
    setPersistedUpload(null);
    updateFormData('cvFile', null);
    setUploadError('');
    localStorage.removeItem('cvUploadResult');
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [setUploadedFiles, updateFormData]);

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback((e) => {
    if (e.target.files?.[0]) {
      handleFileSelect(e.target.files[0]);
    }
  }, [handleFileSelect]);

  const { dragActive, dragHandlers } = useDragAndDrop(handleFileSelect, uploadedFiles.length > 0 || persistedUpload);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    // If there's a previously uploaded file, just continue
    if (persistedUpload) {
      updateFormData('cvFile', persistedUpload);
      onNext();
      return;
    }
    
    // If no files uploaded, skip upload and continue
    if (uploadedFiles.length === 0) {
      updateFormData('cvFile', null);
      onNext();
      return;
    }

    // If files are selected, validate and upload them
    const fileObj = uploadedFiles[0];
    if (!fileObj || !fileObj.file || !(fileObj.file instanceof File)) {
      setUploadError('Invalid file detected. Please remove and re-upload your CV.');
      return;
    }

    setIsSubmitting(true);
    setSaveStatus(null);
    setUploadError('');
    
    try {
      const result = await apiService.uploadFiles(uploadedFiles, user._id);
      const persistentData = {
        name: uploadedFiles[0].name,
        size: uploadedFiles[0].size,
        type: uploadedFiles[0].type,
        uploadDate: new Date().toISOString(),
        filePath: result.filePath,
        record: result.record,
        uploaded: true
      };
      
      localStorage.setItem('cvUploadResult', JSON.stringify(persistentData));
      setPersistedUpload(persistentData);
      updateFormData('cvFile', persistentData);
      setUploadedFiles([]);
      
      setTimeout(() => {
        onNext();
      }, 1000);
      
    } catch (error) {
      setSaveStatus({ 
        type: 'error', 
        message: error.message || 'Failed to upload CV. Please try again.',
        retry: () => {
          setSaveStatus(null);
          handleSubmit(e);
        }
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [uploadedFiles, persistedUpload, setUploadedFiles, updateFormData, onNext, user._id]);

  return (
    <Box sx={{ 
      height: 'calc(100vh - 116px)',
      bgcolor: '#EAE7E6',
      pt: 2,
      px: '80px'
    }}>
      <Container maxWidth={false} sx={{ px: 0, height: '100%' }}>
        <Box sx={{ 
          p: 4, 
          bgcolor: 'white',
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Upload Your CV
            </Typography>
            <Typography variant="body2">
              Please upload your CV to continue (optional)
            </Typography>
          </Box>

          {saveStatus && (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
              <Alert 
                severity={saveStatus.type} 
                sx={{ width: '512px' }}
                onClose={() => setSaveStatus(null)}
                action={
                  saveStatus.type === 'error' && saveStatus.retry ? (
                    <Button color="inherit" size="small" onClick={saveStatus.retry}>
                      Retry
                    </Button>
                  ) : null
                }
              >
                {saveStatus.message}
              </Alert>
            </Box>
          )}

          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            flex: 1
          }}>
            <Box sx={{
              width: '512px',
              display: 'flex',
              flexDirection: 'column',
              gap: '24px'
            }}>
              <form 
                onSubmit={handleSubmit} 
                style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '24px', 
                  flex: 1 
                }}
              >
                <Box sx={{ 
                  flex: 1, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  justifyContent: 'center' 
                }}>
                  <FileUploadZone
                    uploadedFiles={uploadedFiles}
                    persistedUpload={persistedUpload}
                    dragActive={dragActive}
                    dragHandlers={dragHandlers}
                    onBrowseClick={handleBrowseClick}
                    fileInputRef={fileInputRef}
                  />

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={FILE_CONSTRAINTS.ACCEPT_ATTR}
                    onChange={handleFileInputChange}
                    style={{ display: 'none' }}
                    disabled={uploadedFiles.length > 0 || persistedUpload}
                  />

                  {(uploadedFiles.length > 0 || persistedUpload) && (
                    <UploadedFileDisplay
                      file={persistedUpload || uploadedFiles[0]}
                      onRemove={handleRemoveFile}
                      isPersisted={!!persistedUpload}
                    />
                  )}

                  {uploadError && (
                    <Alert 
                      severity="error" 
                      sx={{ mt: 2, fontSize: '14px' }}
                    >
                      {uploadError}
                    </Alert>
                  )}
                  
                  {uploadedFiles.length === 0 && !persistedUpload && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        mt: 2,
                        color: '#666',
                        fontSize: '14px',
                        textAlign: 'center',
                        fontStyle: 'italic'
                      }}
                    >
                      You can skip this step and upload your CV later
                    </Typography>
                  )}
                </Box>

                <FormActions
                  onBack={onBack}
                  isSubmitting={isSubmitting}
                />
              </form>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default UploadCV;